import { useId } from "react";

import { get } from "@/utils/get";
import Valkyrie, { viAngleDown, viAngleUp } from "@sippy-platform/valkyrie";

import { NumberField } from "@base-ui/react/number-field";
import { FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";
import PropTypes from "prop-types";

function normalizeValue(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
}

function InputNumeric({
  id,
  name,
  label,
  value,
  onChange,
  error,
  helperText,
  required,
  disabled,
  startAdornment,
  endAdornment,
  sx,
  ...props
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const hasError = !!get(name ?? id, error) || error === true;
  const message = get(name ?? id, error)?.[0] || helperText;
  const numericValue = normalizeValue(value);

  return (
    <NumberField.Root
      {...props}
      id={inputId}
      name={name}
      value={numericValue}
      required={required}
      disabled={disabled}
      onValueChange={(newValue) => onChange?.(id, newValue)}
      render={(renderProps, state) => (
        <FormControl
          fullWidth
          size="small"
          ref={renderProps.ref}
          disabled={state.disabled}
          required={state.required}
          error={hasError}
          variant="outlined"
        >
          {renderProps.children}
        </FormControl>
      )}
    >
      <InputLabel htmlFor={inputId}>{label}</InputLabel>
      <NumberField.Input
        id={inputId}
        render={(renderProps, state) => (
          <OutlinedInput
            aria-describedby={`${inputId}-helper-text`}
            label={label}
            placeholder={name ?? label}
            inputRef={renderProps.ref}
            value={state.inputValue}
            onBlur={renderProps.onBlur}
            onChange={renderProps.onChange}
            onKeyUp={renderProps.onKeyUp}
            onKeyDown={renderProps.onKeyDown}
            onFocus={renderProps.onFocus}
            slotProps={{
              input: renderProps,
            }}
            startAdornment={startAdornment}
            endAdornment={
              <>
                {endAdornment}
                <InputAdornment
                  position="end"
                  sx={{
                    flexDirection: "column",
                    maxHeight: "unset",
                    alignSelf: "stretch",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    borderStartEndRadius: 8,
                    borderEndEndRadius: 8,
                    contain: "paint",
                    mr: 0.125,
                    "& button": {
                      py: 0,
                      flex: 1,
                      borderRadius: 0,
                    },
                  }}
                >
                  <NumberField.Increment render={<IconButton size="small" aria-label="Increase" />}>
                    <Valkyrie icon={viAngleUp} />
                  </NumberField.Increment>
                  <NumberField.Decrement render={<IconButton size="small" aria-label="Decrease" />}>
                    <Valkyrie icon={viAngleDown} />
                  </NumberField.Decrement>
                </InputAdornment>
              </>
            }
            sx={[{ pr: 0 }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
          />
        )}
      />
      {!!message && <FormHelperText id={`${inputId}-helper-text`}>{message}</FormHelperText>}
    </NumberField.Root>
  );
}

InputNumeric.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.func, PropTypes.object]),
};

export default InputNumeric;
